import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Tabs,
  Row,
  Col,
  Statistic,
  Space,
  Tooltip,
  message,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  TrophyOutlined,
  ExperimentOutlined,
  VideoCameraOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { COLORS } from '../theme';

const { Title, Text } = Typography;

function formatWhen(v: unknown): string {
  if (v == null || v === '') return '—';
  const d = new Date(v as string);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function formatSeconds(sec: unknown): string {
  if (sec == null || sec === '') return '—';
  const n = Number(sec);
  if (!Number.isFinite(n) || n < 0) return '—';
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return `${m}m ${s}s`;
}

const StudentActivityPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<Awaited<
    ReturnType<typeof adminAPI.getStudentActivity>
  >['data'] | null>(null);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminAPI.getStudentActivity(studentId);
        if (!cancelled) setPayload(res.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          message.error('Could not load student activity.');
          setPayload(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const user = payload?.user;
  const summary = payload?.summary;

  const testColumns: ColumnsType<Record<string, unknown>> = useMemo(
    () => [
      {
        title: 'Test',
        key: 'test',
        render: (_, r) => (
          <Space direction="vertical" size={0}>
            <Text strong>{String(r.test_title ?? '—')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {[r.test_subject, r.test_class].filter(Boolean).join(' · ') || '—'}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        width: 120,
        render: (_, r) =>
          r.submitted_at ? (
            <Tag color="success">Completed</Tag>
          ) : (
            <Tag color="processing">In progress</Tag>
          ),
      },
      {
        title: 'Score',
        key: 'score',
        width: 140,
        render: (_, r) => {
          if (!r.submitted_at) return <Text type="secondary">—</Text>;
          const score = r.score != null ? Number(r.score) : null;
          const marks = r.total_marks != null ? Number(r.total_marks) : null;
          const correct = r.correct_count != null ? Number(r.correct_count) : null;
          const totalQ = r.total_questions != null ? Number(r.total_questions) : null;
          const parts: string[] = [];
          if (score != null && marks != null) parts.push(`${score} / ${marks}`);
          else if (score != null) parts.push(String(score));
          if (correct != null && totalQ != null) parts.push(`${correct} correct / ${totalQ} Q`);
          return parts.length ? parts.join(' · ') : '—';
        },
      },
      { title: 'Started', dataIndex: 'started_at', width: 170, render: (v) => formatWhen(v) },
      { title: 'Submitted', dataIndex: 'submitted_at', width: 170, render: (v) => formatWhen(v) },
      {
        title: 'Attempt ID',
        dataIndex: 'id',
        width: 100,
        ellipsis: true,
        render: (v) => (
          <Tooltip title={String(v ?? '')}>
            <Text code style={{ fontSize: 11 }}>
              {String(v ?? '').slice(0, 8)}…
            </Text>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const practiceColumns: ColumnsType<Record<string, unknown>> = useMemo(
    () => [
      {
        title: 'Subject / topic',
        key: 'place',
        render: (_, r) => (
          <Space direction="vertical" size={0}>
            <Text strong>{String(r.subject ?? '—')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {[r.topic, r.subtopic].filter(Boolean).join(' › ') || '—'}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Exam / class',
        key: 'exam',
        width: 140,
        render: (_, r) =>
          [r.exam_type, r.standard].filter(Boolean).join(' · ') || '—',
      },
      { title: 'Difficulty', dataIndex: 'difficulty', width: 110 },
      {
        title: 'Score',
        key: 'sc',
        width: 120,
        render: (_, r) => {
          const sc = r.score != null ? Number(r.score) : null;
          const tq = r.total_questions != null ? Number(r.total_questions) : null;
          if (sc != null && tq != null) return `${sc} / ${tq}`;
          return '—';
        },
      },
      {
        title: 'Time',
        dataIndex: 'total_time_seconds',
        width: 100,
        render: (v) => formatSeconds(v),
      },
      {
        title: 'Status',
        key: 'st',
        width: 120,
        render: (_, r) =>
          r.completed_at ? <Tag color="success">Completed</Tag> : <Tag>In progress</Tag>,
      },
      { title: 'Completed', dataIndex: 'completed_at', width: 170, render: (v) => formatWhen(v) },
    ],
    []
  );

  const liveColumns: ColumnsType<Record<string, unknown>> = useMemo(
    () => [
      {
        title: 'Score',
        key: 'score',
        width: 120,
        render: (_, r) => {
          const pct = r.score != null ? Number(r.score) : null;
          const c = r.correct_answers != null ? Number(r.correct_answers) : null;
          const t = r.total_questions != null ? Number(r.total_questions) : null;
          if (pct != null && t != null) {
            return (
              <Space direction="vertical" size={0}>
                <Text strong>{pct.toFixed(1)}%</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {c ?? '—'} / {t} correct
                </Text>
              </Space>
            );
          }
          return '—';
        },
      },
      {
        title: 'Time',
        dataIndex: 'total_time_seconds',
        width: 100,
        render: (v) => formatSeconds(v),
      },
      { title: 'Completed', dataIndex: 'completed_at', width: 170, render: (v) => formatWhen(v) },
      {
        title: 'Session',
        key: 'session_ref',
        ellipsis: true,
        render: (_, r) => {
          const fs = r.firestore_session_id != null ? String(r.firestore_session_id) : '';
          const sid = r.session_id != null ? String(r.session_id) : '';
          const label = fs || sid || '—';
          return (
            <Tooltip title={label}>
              <Text code style={{ fontSize: 11 }}>
                {label.length > 14 ? `${label.slice(0, 14)}…` : label}
              </Text>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const activityColumns: ColumnsType<Record<string, unknown>> = useMemo(
    () => [
      { title: 'Event', dataIndex: 'activity_type', width: 180 },
      { title: 'When', dataIndex: 'created_at', width: 170, render: (v) => formatWhen(v) },
      { title: 'IP', dataIndex: 'ip_address', width: 130, render: (v) => v || '—' },
      {
        title: 'Details',
        dataIndex: 'metadata',
        ellipsis: true,
        render: (v) => {
          if (v == null) return '—';
          const text =
            typeof v === 'object' ? JSON.stringify(v) : String(v);
          return (
            <Tooltip title={<pre style={{ maxWidth: 420 }}>{text}</pre>}>
              <Text type="secondary">{text.length > 80 ? `${text.slice(0, 80)}…` : text}</Text>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  if (!studentId) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user || !summary) {
    return (
      <Card>
        <Text type="danger">Student not found or failed to load.</Text>
        <div style={{ marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/students')}>
            Back to students
          </Button>
        </div>
      </Card>
    );
  }

  const tabItems = [
    {
      key: 'tests',
      label: (
        <span>
          <TrophyOutlined /> Tests ({payload!.testAttempts.length})
        </span>
      ),
      children: (
        <Table
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          columns={testColumns}
          dataSource={payload!.testAttempts}
          scroll={{ x: 900 }}
        />
      ),
    },
    {
      key: 'practice',
      label: (
        <span>
          <ExperimentOutlined /> Practice ({payload!.practiceSessions.length})
        </span>
      ),
      children: (
        <Table
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          columns={practiceColumns}
          dataSource={payload!.practiceSessions}
          scroll={{ x: 960 }}
        />
      ),
    },
    {
      key: 'live',
      label: (
        <span>
          <VideoCameraOutlined /> Live tests ({payload!.liveTestResults.length})
        </span>
      ),
      children: (
        <Table
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          columns={liveColumns}
          dataSource={payload!.liveTestResults}
          scroll={{ x: 720 }}
        />
      ),
    },
    {
      key: 'activity',
      label: (
        <span>
          <HistoryOutlined /> Activity log ({payload!.activityLog.length})
        </span>
      ),
      children: (
        <Table
          rowKey="id"
          size="small"
          pagination={{ pageSize: 15, showSizeChanger: true }}
          columns={activityColumns}
          dataSource={payload!.activityLog}
          scroll={{ x: 800 }}
        />
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/students')}
            style={{ paddingLeft: 0, marginBottom: 8 }}
          >
            My Students
          </Button>
          <Title level={3} style={{ margin: 0, color: COLORS.text }}>
            {String(user.full_name ?? 'Student')}
          </Title>
          <Space wrap size="large" style={{ marginTop: 8 }}>
            <Text type="secondary">{String(user.email ?? '')}</Text>
            {user.phone_number ? <Text type="secondary">{String(user.phone_number)}</Text> : null}
            {user.college ? <Text type="secondary">{String(user.college)}</Text> : null}
            <Tag color={user.is_premium ? 'gold' : 'blue'}>
              {user.is_premium ? 'Premium' : 'Free'}
            </Tag>
            <Text type="secondary">Joined {formatWhen(user.created_at)}</Text>
            {user.last_login_at ? (
              <Text type="secondary">Last login {formatWhen(user.last_login_at)}</Text>
            ) : null}
          </Space>
        </div>

        <Card style={{ borderRadius: 12, border: `1px solid ${COLORS.border}` }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Tests submitted" value={summary.testsSubmitted} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {summary.testsInProgress} in progress · {summary.testAttemptsTotal} total attempts
              </Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Practice completed" value={summary.practiceCompleted} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {summary.practiceInProgress} in progress · {summary.practiceSessionsTotal} sessions
              </Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Live tests" value={summary.liveTestsCompleted} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Saved results in this system
              </Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic title="Activity events" value={summary.activityEvents} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Recent log rows (max 200)
              </Text>
            </Col>
          </Row>
        </Card>

        <Card
          style={{ borderRadius: 12, border: `1px solid ${COLORS.border}` }}
          bodyStyle={{ paddingTop: 8 }}
        >
          <Tabs defaultActiveKey="tests" items={tabItems} />
        </Card>
      </Space>
    </div>
  );
};

export default StudentActivityPage;
